// src/components/Eventos/HeatDetails.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Grid,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
  List,
  ListItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsIcon from '@mui/icons-material/Settings';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FlagIcon from '@mui/icons-material/Flag';
import CheckIcon from '@mui/icons-material/CheckCircle';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GavelIcon from '@mui/icons-material/Gavel';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import {
  useEventContext,
  TrackEventCodes,
  fieldVerticalEvents,
  fieldHorizontalEvents,
} from '../../context/EventContext';
import EventOptions from './EventOptions';
import VerticalEvents from './VerticalEvents';
import HorizontalEvents from './HorizontalEvents';
import TrackEvents from './TrackEvents';
import HeatSwitcher from './HeatSwitcher';
import BoardSelectorModal from '../Eventos/BoardSelectorModal';
import EventStatusControl from './EventStatusControl';

// Estilos unificados para los botones
const getUnifiedButtonStyles = (theme, variant = 'contained') => ({
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  textTransform: 'none',
  fontWeight: 600,
  minHeight: '44px',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
});

export default function HeatDetails() {
  const { competitionId, eventNumber, round, heat } = useParams();
  const en = Number(eventNumber), ro = Number(round), he = Number(heat);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileMenu = useMediaQuery('(max-width:851px)'); // Nuevo breakpoint para menú móvil

  // context, estados y efectos
  const {
    evtData,
    loading,
    error,
    getParticipantResults,
    getTestSettings,
    getHeatImages,
    boardConfigs, 
    openTablero, 
    updateBoardConfig,
    refreshTablero,
    participantsResults,
    setHeatStatus,
    setResultStatus, // Función que ya existe para cambiar el estado del resultado
  } = useEventContext();

  const [heatDetails, setHeatDetails] = useState(null);
  const [editingResults, setEditingResults] = useState(false);
  const [editingByLane, setEditingByLane] = useState(false);
  const [isChangingHeat, setIsChangingHeat] = useState(false);
  const [prevHeat, setPrevHeat] = useState(null);
  const [heatImages, setHeatImages] = useState([]);
  const [loadingImgs, setLoadingImgs] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openBoardSelector, setOpenBoardSelector] = useState(false);
  
  // NUEVOS Estados para el menú móvil
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [showResultsSubmenu, setShowResultsSubmenu] = useState(false);
  const [openEventOptionsModal, setOpenEventOptionsModal] = useState(false);

  const heatKey = `${eventNumber}_${round}_${heat}`;

  // NUEVO: Obtener estado del heat para el menú móvil
  const res = participantsResults[heatKey] || {};
  const heatStatus = res.status || 'not_started';
  const startTime = res.startTime?.toDate?.() ?? null;
  const endTime = res.endTime?.toDate?.() ?? null;

  // 1) cargar heat
  useEffect(() => {
    if (prevHeat !== null && prevHeat !== he) setIsChangingHeat(true);
    setPrevHeat(he);
    if (!evtData) return;

    const found = evtData.find(ev =>
      ev.eventNumber === en &&
      ev.round === ro &&
      ev.heat === he
    );
    if (!found) {
      setHeatDetails(null);
      setIsChangingHeat(false);
      return;
    }
    const participants = (found.participants || []).map(p => {
      const pr = getParticipantResults(en, ro, he, p.idNumber) || {};
      return {
        ...p,
        fullName: p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' '),
        time: pr.time ?? '',
        wind: pr.wind ?? null,
        special: pr.special ?? '',
        attempts: pr.attempts ?? [],
        reactionTime: pr.reactionTime ?? null,
        lapTimes: pr.lapTimes ?? [],
      };
    });
    const testSettings = getTestSettings(en, ro);
    setHeatDetails({ ...found, testSettings, participants });
    if (isChangingHeat) {
      const t = setTimeout(() => setIsChangingHeat(false), 500);
      return () => clearTimeout(t);
    } else {
      setIsChangingHeat(false);
    }
  }, [evtData, en, ro, he, getTestSettings, getParticipantResults, prevHeat, isChangingHeat]);

  // 2) cargar imágenes
  useEffect(() => {
    if (!heatDetails) return;
    setLoadingImgs(true);
    getHeatImages(en, ro, he)
      .then(urls => setHeatImages(urls))
      .catch(console.error)
      .finally(() => setLoadingImgs(false));
  }, [heatDetails, en, ro, he, getHeatImages]);

  // 3) hooks que antes estaban abajo: siempre se ejecutan
  const lanesList = useMemo(() => {
    if (!heatDetails) return [];
    return Array.from(
      new Set(
        heatDetails.participants
          .map(p => parseInt(p.lane, 10))
          .filter(n => !isNaN(n))
      )
    ).sort((a, b) => a - b);
  }, [heatDetails]);

  const heatsOfEvent = useMemo(() => {
    if (!evtData) return [];
    return evtData
      .filter(ev => ev.eventNumber === en && ev.round === ro)
      .map(ev => ev.heat)
      .sort((a, b) => a - b);
  }, [evtData, en, ro]);

  const isVerticalEvent = heatDetails?.eventCode && fieldVerticalEvents.includes(heatDetails.eventCode);
  const isHorizontalFieldEvent = heatDetails?.eventCode && fieldHorizontalEvents.includes(heatDetails.eventCode);
  const isTrackEvent = heatDetails?.eventCode && TrackEventCodes.includes(heatDetails.eventCode);

  const eventCategory = useMemo(() => {
    if (!heatDetails) return null;
    if (isTrackEvent)              return 'track';
    if (isHorizontalFieldEvent)    return 'horizontalField';
    if (isVerticalEvent)           return 'verticalField';
    return null;
  }, [heatDetails, isTrackEvent, isHorizontalFieldEvent, isVerticalEvent]);

  // 4) helpers
  const handleGoBack = () => navigate(`/${competitionId}/integraciones/finishlynx`);

  const handleAssignToHeat = config => {
    updateBoardConfig(config.id, {
      'settings.assignedHeat': heatKey
    })
    .then(() => {
      setOpenBoardSelector(false);
    })
    .catch(error => {
      console.error('[HeatDetails] Error al asignar tablero:', error);
    });
  };

  const handleUnassignBoard = useCallback((config) => {
    updateBoardConfig(config.id, {
      'settings.assignedHeat': null
    })
    .then(() => {
      console.log('[HeatDetails] Tablero desasignado exitosamente');
    })
    .catch(error => {
      console.error('[HeatDetails] Error al desasignar tablero:', error);
    });
  }, [updateBoardConfig]);

  const handleOpenBoard = useCallback((config) => {
    if (!heatDetails) {
      console.error('[HeatDetails] No hay datos del heat disponibles');
      return;
    }
    if (!heatDetails.participants || heatDetails.participants.length === 0) {

    }    
    const freshConfig = boardConfigs.find(c => c.id === config.id);
    if (!freshConfig) {
      console.error('[HeatDetails] No se encontró la configuración fresca en boardConfigs');
      return;
    }
    
    localStorage.removeItem(`config-${config.id}`);
    localStorage.setItem(`config-${config.id}`, JSON.stringify(freshConfig));
    
    openTablero(freshConfig.id, heatDetails);
    setOpenBoardSelector(false);
  }, [heatDetails, openTablero, boardConfigs]);

  const handleRefreshBoards = useCallback(() => {
    if (!heatDetails) return;
    
    boardConfigs
      .filter(cfg => cfg.settings?.assignedHeat === heatKey)
      .forEach(cfg => {
        localStorage.setItem(`config-${cfg.id}`, JSON.stringify(cfg));
        refreshTablero(cfg.id, heatDetails);
      });
  }, [boardConfigs, heatDetails, heatKey, refreshTablero]);

  useEffect(() => {
    if (heatDetails && !isChangingHeat) {
      const timer = setTimeout(() => {
        handleRefreshBoards();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [heatDetails, isChangingHeat, handleRefreshBoards]);

  // NUEVOS Handlers para el menú móvil
  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleOpenEventOptions = () => {
    handleMobileMenuClose();
    setOpenEventOptionsModal(true);
  };

  const handleOpenBoardSelectorMobile = () => {
    handleMobileMenuClose();
    setOpenBoardSelector(true);
  };

  const handleToggleResultsSubmenu = () => {
    setShowResultsSubmenu(!showResultsSubmenu);
  };

  const handleSetResultStatus = (status) => {
    handleMobileMenuClose();
    
    // Mapear los valores del menú móvil a los valores que espera la función
    const statusMap = {
      'no_oficial': 'unofficial',
      'oficial': 'official', 
      'bajo_protesta': 'under_protest'
    };
    
    const mappedStatus = statusMap[status];
    
    if (mappedStatus) {
      // Usar la misma función que usa el modo escritorio
      setResultStatus(en, ro, he, mappedStatus);
      console.log('Estado del resultado actualizado a:', mappedStatus);
    } else {
      console.error('Estado no válido:', status);
    }
  };

  const handleStartHeat = async () => {
    handleMobileMenuClose();
    await setHeatStatus(heatDetails.eventNumber, heatDetails.round, heatDetails.heat, 'in_progress');
  };

  const handleFinishHeat = async () => {
    handleMobileMenuClose();
    await setHeatStatus(heatDetails.eventNumber, heatDetails.round, heatDetails.heat, 'finished');
  };

  const handleUploadFile = () => {
    handleMobileMenuClose();
    console.log('Subir archivo');
  };

  const handleDownloadFile = () => {
    handleMobileMenuClose();
    console.log('Descargar archivo');
  };

  const { enableWind } = getTestSettings(en, ro);

  // 5) render condicional
  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link onClick={handleGoBack} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <ArrowBackIcon sx={{ mr: 0.5 }} /> Eventos
          </Link>
          <Typography>Evento {eventNumber}</Typography>
          <Typography>Ronda {round}</Typography>
        </Breadcrumbs>
        <Box sx={{ mt: 4, textAlign: 'center' }}><CircularProgress /></Box>
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link onClick={handleGoBack} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <ArrowBackIcon sx={{ mr: 0.5 }} /> Eventos
          </Link>
          <Typography>Evento {eventNumber}</Typography>
          <Typography>Ronda {round}</Typography>
        </Breadcrumbs>
        <Typography sx={{ mt: 4 }} color="error">{error}</Typography>
      </Box>
    );
  }
  if (!heatDetails) {
    return (
      <Box sx={{ p: 2 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link onClick={handleGoBack} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <ArrowBackIcon sx={{ mr: 0.5 }} /> Eventos
          </Link>
          <Typography>Evento {eventNumber}</Typography>
          <Typography>Ronda {round}</Typography>
        </Breadcrumbs>
        <Typography sx={{ mt: 4 }} color="error">No se encontraron datos para este heat.</Typography>
      </Box>
    );
  }

  // 6) render principal
  return (
    <Box sx={{ p: 2 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link onClick={handleGoBack} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <ArrowBackIcon sx={{ mr: 0.5 }} /> Eventos
        </Link>
        <Typography>Evento {eventNumber}</Typography>
        <Typography>Ronda {round}</Typography>
        <Typography>Heat {heat}</Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 3 }}>
        <Typography 
          variant={isMobileMenu ? "h5" : "h4"} 
          gutterBottom
          sx={{ 
            fontSize: isMobileMenu ? '1.5rem' : '2.125rem',
          }}
        >
          {heatDetails.eventName}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip label={`Evento: ${eventNumber}`} size="small" variant="outlined" color="primary" />
          <Chip label={`Ronda: ${round}`} size="small" variant="outlined" color="primary" />
          <Chip label={`Heat: ${heat}`} size="small" variant="outlined" color="primary" sx={{ fontWeight: 'bold' }} />
          {!!heatDetails.participants.length && (
            <Chip label={`${heatDetails.participants.length} participantes`} size="small" variant="outlined" color="secondary" />
          )}
          {!!heatImages.length && (
            <IconButton onClick={() => setOpenModal(true)} title="Ver Fotofinish">
              <CameraAltIcon />
            </IconButton>
          )}
        </Box>
        {!!heatsOfEvent.length && heatsOfEvent.length > 1 && (
          <Box sx={{ mt: 2 }}>
            <HeatSwitcher
              competitionId={competitionId}
              eventNumber={eventNumber}
              round={round}
              heats={heatsOfEvent}
              currentHeat={he}
              isLoading={isChangingHeat}
            />
          </Box>
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 3 }}>
            {/* CONDICION: Mostrar menú móvil o botones originales */}
            {isMobileMenu ? (
              // NUEVO: Menú móvil
              <Box>
                {/* Chip de estado arriba del botón */}
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  {heatStatus === 'finished' && endTime && (
                    <Chip
                      icon={<CheckIcon />}
                      label={`Finalizado ${endTime.toLocaleTimeString()}`}
                      color="success"
                      variant="filled"
                      size="small"
                    />
                  )}
                  {heatStatus === 'in_progress' && startTime && (
                    <Chip
                      icon={<HourglassTopIcon />}
                      label={`Iniciado ${startTime.toLocaleTimeString()}`}
                      color="warning"
                      variant="filled"
                      size="small"
                    />
                  )}
                  {heatStatus === 'not_started' && (
                    <Chip
                      icon={<AccessTimeIcon />}
                      label="No iniciado"
                      color="default"
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>

                <Button
                  variant="contained"
                  startIcon={<SettingsIcon />}
                  endIcon={<MoreVertIcon />}
                  onClick={handleMobileMenuOpen}
                  fullWidth
                  sx={getUnifiedButtonStyles(theme)}
                >
                  Opciones
                </Button>
                
                <Menu
                  anchorEl={mobileMenuAnchor}
                  open={Boolean(mobileMenuAnchor)}
                  onClose={handleMobileMenuClose}
                  PaperProps={{
                    sx: {
                      minWidth: 250,
                      maxHeight: 400,
                    }
                  }}
                >
                  {/* Ajustes de Prueba */}
                  <MenuItem onClick={handleOpenEventOptions}>
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Ajustes de Prueba</ListItemText>
                  </MenuItem>

                  {/* Seleccionar Tablero */}
                  <MenuItem onClick={handleOpenBoardSelectorMobile}>
                    <ListItemIcon>
                      <OpenInNewIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Seleccionar Tablero</ListItemText>
                  </MenuItem>

                  <Divider />

                  {/* Submenú de Resultado - SOLO cuando la competencia esté terminada */}
                  {heatStatus === 'finished' && (
                    <>
                      <MenuItem onClick={handleToggleResultsSubmenu}>
                        <ListItemIcon>
                          <GavelIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Resultado</ListItemText>
                        {showResultsSubmenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </MenuItem>
                      
                      <Collapse in={showResultsSubmenu} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          <ListItem sx={{ pl: 4 }}>
                            <MenuItem onClick={() => handleSetResultStatus('no_oficial')} sx={{ width: '100%', pl: 0 }}>
                              <ListItemText primary="No oficial" />
                            </MenuItem>
                          </ListItem>
                          <ListItem sx={{ pl: 4 }}>
                            <MenuItem onClick={() => handleSetResultStatus('oficial')} sx={{ width: '100%', pl: 0 }}>
                              <ListItemText primary="Oficial" />
                            </MenuItem>
                          </ListItem>
                          <ListItem sx={{ pl: 4 }}>
                            <MenuItem onClick={() => handleSetResultStatus('bajo_protesta')} sx={{ width: '100%', pl: 0 }}>
                              <ListItemText primary="Bajo protesta" />
                            </MenuItem>
                          </ListItem>
                        </List>
                      </Collapse>

                      <Divider />
                    </>
                  )}

                  {/* Botones de control de estado */}
                  {heatStatus === 'not_started' && (
                    <MenuItem onClick={handleStartHeat}>
                      <ListItemIcon>
                        <PlayArrowIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText>Iniciar Competencia</ListItemText>
                    </MenuItem>
                  )}

                  {heatStatus === 'in_progress' && (
                    <MenuItem onClick={handleFinishHeat}>
                      <ListItemIcon>
                        <FlagIcon fontSize="small" color="error" />
                      </ListItemIcon>
                      <ListItemText>Terminar Competencia</ListItemText>
                    </MenuItem>
                  )}

                  {heatStatus === 'finished' && (
                    <MenuItem disabled>
                      <ListItemIcon>
                        <CheckIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText>Competencia Finalizada</ListItemText>
                    </MenuItem>
                  )}

                  <Divider />

                  {/* Opciones de archivo */}
                  <MenuItem onClick={handleUploadFile}>
                    <ListItemIcon>
                      <UploadIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Subir Archivo LFF/LIF</ListItemText>
                  </MenuItem>

                  <MenuItem onClick={handleDownloadFile}>
                    <ListItemIcon>
                      <DownloadIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Descargar Archivo LFF/LIF</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            ) : (
              // ORIGINAL: Botones de escritorio (sin cambios)
              <Stack 
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                flexWrap="wrap"
              >
                <EventOptions
                  heatDetails={heatDetails}
                  totalHeats={heatsOfEvent.length}
                  editingResults={editingResults}
                  setEditingResults={setEditingResults}
                  editingByLane={editingByLane}
                  setEditingByLane={setEditingByLane}
                  windEnabled={enableWind}
                  unifiedStyles={getUnifiedButtonStyles(theme)}
                  hiddenInMobile={isMobileMenu} // Ocultar el botón cuando está en modo móvil
                  // Props para modo móvil
                  open={openEventOptionsModal}
                  onClose={() => setOpenEventOptionsModal(false)}
                  mobileMode={isMobileMenu}
                />
                
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => setOpenBoardSelector(true)}
                  fullWidth={isMobile}
                  sx={getUnifiedButtonStyles(theme, 'outlined')}
                >
                  Seleccionar tablero
                </Button>

                <EventStatusControl 
                  heatDetails={heatDetails} 
                  unifiedStyles={getUnifiedButtonStyles(theme)}
                />
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          {isVerticalEvent && (
            <VerticalEvents
              heatDetails={heatDetails}
              lanesList={lanesList}
              editingResults={editingResults}
              editingByLane={editingByLane}
              setEditingResults={setEditingResults}
              setEditingByLane={setEditingByLane}
            />
          )}
          {isHorizontalFieldEvent && (
            <HorizontalEvents
              heatDetails={heatDetails}
              lanesList={lanesList}
              editingResults={editingResults}
              editingByLane={editingByLane}
              setEditingResults={setEditingResults}
              setEditingByLane={setEditingByLane}
            />
          )}
          {isTrackEvent && (
            <TrackEvents
              heatDetails={heatDetails}
              lanesList={lanesList}
              editingResults={editingResults}
              editingByLane={editingByLane}
              setEditingResults={setEditingResults}
              setEditingByLane={setEditingByLane}
            />
          )}
        </Grid>
      </Grid>

      {/* Los modales van aquí - SIN duplicar EventOptions */}
      {isMobileMenu && (
        <EventOptions
          heatDetails={heatDetails}
          totalHeats={heatsOfEvent.length}
          editingResults={editingResults}
          setEditingResults={setEditingResults}
          editingByLane={editingByLane}
          setEditingByLane={setEditingByLane}
          windEnabled={enableWind}
          unifiedStyles={getUnifiedButtonStyles(theme)}
          open={openEventOptionsModal}
          onClose={() => setOpenEventOptionsModal(false)}
          mobileMode={true}
        />
      )}
      <BoardSelectorModal
        open={openBoardSelector}
        onClose={() => setOpenBoardSelector(false)}
        configs={boardConfigs}
        eventCategory={eventCategory}
        currentHeatKey={heatKey}
        onOpenBoard={handleOpenBoard}
        onAssign={handleAssignToHeat}
        onUnassign={handleUnassignBoard}
        heatDetails={heatDetails}
      />

      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1 }}>
          <Typography variant="h6">Fotofinish – {heatDetails.eventName} Heat {heat}</Typography>
          <IconButton onClick={() => setOpenModal(false)}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pt: 1 }}>
          {heatImages.map((url, i) => (
            <Box
              key={i}
              component="img"
              src={url}
              alt={`Fotofinish ${i+1}`}
              sx={{ width: isMobile ? '100%' : 300, borderRadius: 1 }}
            />
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1 }}>
          <Button onClick={() => setOpenModal(false)} variant="contained">Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}